import { X } from 'lucide-react';
import { useRef, useState, type KeyboardEvent } from 'react';

import { cn } from '@/utils/cn';

/**
 * Free-text tokens: skills on a volunteer, tags on a sermon, keywords on a group.
 *
 * Enter and comma both commit, because people paste comma-separated lists and expect
 * them to split. Backspace on an empty field removes the last token, which is the one
 * interaction that makes a tag field feel like a text field rather than a widget.
 */

export interface TagInputProps {
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  removeLabel?: string;
  maxTags?: number;
  disabled?: boolean;
  invalid?: boolean;
  id?: string;
  className?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
}

export const TagInput = ({
  value,
  onChange,
  placeholder,
  removeLabel,
  maxTags,
  disabled = false,
  invalid = false,
  id,
  className,
  ...aria
}: TagInputProps) => {
  const [draft, setDraft] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const add = (raw: string) => {
    const candidates = raw
      .split(',')
      .map((tag) => tag.trim())
      .filter(Boolean)
      // Case-insensitive de-duplication: "Choir" and "choir" are one tag, and a list
      // that lets both in produces two of every filter.
      .filter(
        (tag) => !value.some((existing) => existing.toLowerCase() === tag.toLowerCase()),
      );

    if (candidates.length === 0) return;
    const room = maxTags === undefined ? candidates.length : maxTags - value.length;
    if (room <= 0) return;

    onChange([...value, ...candidates.slice(0, room)]);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      add(draft);
      setDraft('');
      return;
    }
    if (event.key === 'Backspace' && draft === '' && value.length > 0) {
      onChange(value.slice(0, -1));
    }
  };

  const full = maxTags !== undefined && value.length >= maxTags;

  return (
    <div
      onClick={() => inputRef.current?.focus()}
      className={cn(
        'flex min-h-10 w-full flex-wrap items-center gap-1.5 rounded-md border bg-surface px-2 py-1.5',
        'focus-within:border-brand',
        invalid ? 'border-danger' : 'border-border hover:border-border-strong',
        disabled && 'bg-surface-sunken',
        className,
      )}
    >
      {value.map((tag) => (
        <span
          key={tag}
          className="flex items-center gap-1 rounded-sm bg-surface-sunken px-1.5 py-0.5 text-body-sm text-fg"
        >
          {tag}
          {disabled ? null : (
            <button
              type="button"
              aria-label={removeLabel}
              onClick={(event) => {
                event.stopPropagation();
                onChange(value.filter((current) => current !== tag));
              }}
              className="text-fg-tertiary hover:text-fg"
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </span>
      ))}

      <input
        ref={inputRef}
        id={id}
        value={draft}
        disabled={disabled || full}
        placeholder={value.length === 0 ? placeholder : undefined}
        aria-invalid={invalid || undefined}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={onKeyDown}
        onBlur={() => {
          // Committing on blur, because a typed tag left uncommitted is silently lost
          // when the form saves.
          add(draft);
          setDraft('');
        }}
        className="min-w-24 flex-1 bg-transparent px-1 text-body text-fg outline-none placeholder:text-fg-tertiary"
        {...aria}
      />
    </div>
  );
};
